import type { PetRelationshipKind } from "@/pages/daycare-hotel/reservation/petProfileModel";

export type DraftAlert = {
  id: string;
  label: string;
  value: string;
  color: string | null;
  removed?: boolean;
};

export type DraftStoredItem = {
  id: string;
  itemName: string;
  storageLocation: string | null;
  removed?: boolean;
};

export type DraftRelationship = {
  id: string;
  relatedPetId: string;
  relationship: PetRelationshipKind;
  removed?: boolean;
};

export type DraftRecurringActivity = {
  id: string;
  typeId: string;
  typeName: string;
  typeColor: string | null;
  typeIcon: string | null;
  description: string;
  scheduledTime: string | null;
  recurring: boolean;
  removed?: boolean;
};

export type PetDaycareHotelDraft = {
  notes: string;
  packIds: string[];
  alerts: DraftAlert[];
  storedItems: DraftStoredItem[];
  relationships: DraftRelationship[];
  recurringActivities: DraftRecurringActivity[];
};

export const EMPTY_PET_DAYCARE_HOTEL_DRAFT: PetDaycareHotelDraft = {
  notes: "",
  packIds: [],
  alerts: [],
  storedItems: [],
  relationships: [],
  recurringActivities: [],
};

const TEMP_PREFIX = "temp:";

let tempCounter = 0;

export function draftTempId() {
  tempCounter += 1;
  return `${TEMP_PREFIX}${tempCounter}`;
}

export function isDraftTempId(id: string) {
  return id.startsWith(TEMP_PREFIX);
}

export type PetDaycareHotelOperation =
  | { kind: "add-pack-membership"; packId: string }
  | { kind: "remove-pack-membership"; packId: string }
  | { kind: "create-alert"; label: string; value: string; color: string | null }
  | { kind: "update-alert"; id: string; label: string; value: string; color: string | null }
  | { kind: "deactivate-alert"; id: string }
  | { kind: "create-stored-item"; itemName: string; storageLocation: string | null }
  | { kind: "update-stored-item"; id: string; itemName: string; storageLocation: string | null }
  | { kind: "unkeep-stored-item"; id: string }
  | { kind: "create-relationship"; relatedPetId: string; relationship: PetRelationshipKind }
  | { kind: "delete-relationship"; id: string }
  | { kind: "create-recurring-activity"; typeId: string; description: string; scheduledTime: string | null }
  | { kind: "update-recurring-activity"; id: string; typeId: string; description: string; scheduledTime: string | null }
  | { kind: "deactivate-recurring-activity"; id: string };

function byId<T extends { id: string }>(rows: T[]) {
  return new Map(rows.map((row) => [row.id, row]));
}

function trimmed(value: string | null) {
  return (value ?? "").trim();
}

function normalizedLocation(value: string | null) {
  const location = trimmed(value);
  return location.length > 0 ? location : null;
}

function normalizedTime(value: string | null) {
  const time = trimmed(value);
  return time.length > 0 ? time : null;
}

function reconcilePackMemberships(initial: string[], current: string[]): PetDaycareHotelOperation[] {
  const previous = new Set(initial);
  const selected = new Set(current);
  const operations: PetDaycareHotelOperation[] = [];

  for (const packId of current) {
    if (!previous.has(packId)) operations.push({ kind: "add-pack-membership", packId });
  }
  for (const packId of initial) {
    if (!selected.has(packId)) operations.push({ kind: "remove-pack-membership", packId });
  }

  return operations;
}

function reconcileAlerts(initial: DraftAlert[], current: DraftAlert[]): PetDaycareHotelOperation[] {
  const previous = byId(initial);
  const operations: PetDaycareHotelOperation[] = [];

  for (const alert of current) {
    const value = trimmed(alert.value);
    const existing = previous.get(alert.id);

    if (alert.removed) {
      if (existing) operations.push({ kind: "deactivate-alert", id: alert.id });
      continue;
    }
    if (value.length === 0) continue;

    if (!existing) {
      operations.push({ kind: "create-alert", label: alert.label, value, color: alert.color });
      continue;
    }
    if (existing.value !== value || existing.label !== alert.label || existing.color !== alert.color) {
      operations.push({ kind: "update-alert", id: alert.id, label: alert.label, value, color: alert.color });
    }
  }

  return operations;
}

function reconcileStoredItems(
  initial: DraftStoredItem[],
  current: DraftStoredItem[],
): PetDaycareHotelOperation[] {
  const previous = byId(initial);
  const operations: PetDaycareHotelOperation[] = [];

  for (const item of current) {
    const itemName = trimmed(item.itemName);
    const storageLocation = normalizedLocation(item.storageLocation);
    const existing = previous.get(item.id);

    if (item.removed) {
      if (existing) operations.push({ kind: "unkeep-stored-item", id: item.id });
      continue;
    }
    if (itemName.length === 0) continue;

    if (!existing) {
      operations.push({ kind: "create-stored-item", itemName, storageLocation });
      continue;
    }
    if (existing.itemName !== itemName || normalizedLocation(existing.storageLocation) !== storageLocation) {
      operations.push({ kind: "update-stored-item", id: item.id, itemName, storageLocation });
    }
  }

  return operations;
}

function reconcileRelationships(
  initial: DraftRelationship[],
  current: DraftRelationship[],
): PetDaycareHotelOperation[] {
  const previous = byId(initial);
  const operations: PetDaycareHotelOperation[] = [];

  for (const relationship of current) {
    const existing = previous.get(relationship.id);

    if (relationship.removed) {
      if (existing) operations.push({ kind: "delete-relationship", id: relationship.id });
      continue;
    }
    if (relationship.relatedPetId.length === 0) continue;

    if (!existing) {
      operations.push({
        kind: "create-relationship",
        relatedPetId: relationship.relatedPetId,
        relationship: relationship.relationship,
      });
      continue;
    }
    // A linha do banco é chaveada pelo par de pets; mudar pet ou tipo vira apagar e recriar.
    if (
      existing.relatedPetId !== relationship.relatedPetId ||
      existing.relationship !== relationship.relationship
    ) {
      operations.push({ kind: "delete-relationship", id: relationship.id });
      operations.push({
        kind: "create-relationship",
        relatedPetId: relationship.relatedPetId,
        relationship: relationship.relationship,
      });
    }
  }

  return operations;
}

function reconcileRecurringActivities(
  initial: DraftRecurringActivity[],
  current: DraftRecurringActivity[],
): PetDaycareHotelOperation[] {
  const previous = byId(initial);
  const operations: PetDaycareHotelOperation[] = [];

  for (const activity of current) {
    const existing = previous.get(activity.id);
    const description = trimmed(activity.description);
    const scheduledTime = normalizedTime(activity.scheduledTime);

    if (activity.removed || !activity.recurring) {
      if (existing) operations.push({ kind: "deactivate-recurring-activity", id: activity.id });
      continue;
    }
    if (activity.typeId.length === 0 || description.length === 0) continue;

    if (!existing || isDraftTempId(activity.id)) {
      operations.push({ kind: "create-recurring-activity", typeId: activity.typeId, description, scheduledTime });
      continue;
    }

    if (
      existing.typeId !== activity.typeId
      || trimmed(existing.description) !== description
      || normalizedTime(existing.scheduledTime) !== scheduledTime
    ) {
      operations.push({
        kind: "update-recurring-activity",
        id: activity.id,
        typeId: activity.typeId,
        description,
        scheduledTime,
      });
    }
  }

  return operations;
}

export function reconcilePetDaycareHotelDraft(
  initial: PetDaycareHotelDraft,
  current: PetDaycareHotelDraft,
): PetDaycareHotelOperation[] {
  return [
    ...reconcilePackMemberships(initial.packIds, current.packIds),
    ...reconcileAlerts(initial.alerts, current.alerts),
    ...reconcileStoredItems(initial.storedItems, current.storedItems),
    ...reconcileRelationships(initial.relationships, current.relationships),
    ...reconcileRecurringActivities(initial.recurringActivities, current.recurringActivities),
  ];
}
